// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { GridColDef, GridRenderCellParams, GridToolbarContainer } from '@mui/x-data-grid';
import { ProviderConfiguration } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Configuration/Provider';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { SyncButton } from '../../../components/Button/SyncButton';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import { useParties } from '../../../contexts/PartiesContext';
import useArchiveProviderConfiguration from '../../../hooks/mutations/registry/configuration/useArchiveProviderConfiguration';
import useRegistryProviderConfiguration from '../../../hooks/queries/registry/configuration/useRegistryProviderConfiguration';
import useRegistryProviderConfigurations from '../../../hooks/queries/registry/configuration/useRegistryProviderConfigurations';
import useProviderService from '../../../hooks/queries/registry/onboarding/useProviderService';
import RequiredCredentials from '../../../modules/Credential/RequiredCredentials';
import TemplateContract from '../../../utils/TemplateContract';
import { ERR_ARC_PROVIDER_CONF } from '../../../utils/strings';
import { PageWrapper } from '../../PageWrapper';
import { CreateProviderConfigurationDialog } from './CreateProviderConfigurationDialog';
import { ModifyProviderConfigurationDialog } from './ModifyProviderConfigurationDialog';

type Row = TemplateContract<ProviderConfiguration>;

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'operator',
      headerName: 'Operator',
      renderCell: (params) => <PartyDisplay party={params.row.payload.operator} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.payload.operator,
    },
    {
      field: 'provider',
      headerName: 'Provider',
      renderCell: (params) => <PartyDisplay party={params.row.payload.provider} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.payload.provider,
    },
    {
      field: 'holderRequirement',
      headerName: 'Holder Requirement',
      renderCell: (params) => (
        <RequiredCredentials credentialRequirements={params.row.payload.holderRequirements} />
      ),
      flex: 250,
      minWidth: 200,
      filterable: false,
      sortable: false,
    },
    {
      field: 'registrarRequirement',
      headerName: 'Registrar Requirement',
      renderCell: (params) => (
        <RequiredCredentials credentialRequirements={params.row.payload.registrarRequirements} />
      ),
      flex: 250,
      minWidth: 200,
      filterable: false,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

const Toolbar = () => {
  const [createProviderConfigurationIsOpen, setCreateProviderConfigurationIsOpen] = useState(false);
  const configuration = useRegistryProviderConfiguration();

  const { isLoading } = configuration;

  return (
    <GridToolbarContainer>
      {isLoading ? null : (
        <>
          {createProviderConfigurationIsOpen && (
            <CreateProviderConfigurationDialog
              open={createProviderConfigurationIsOpen}
              onClose={() => setCreateProviderConfigurationIsOpen(false)}
            />
          )}
          <SyncButton
            onClick={() => setCreateProviderConfigurationIsOpen(true)}
            disabled={!!configuration.data}
          >
            Create provider configuration
          </SyncButton>
        </>
      )}
    </GridToolbarContainer>
  );
};

export const ProviderConfigurations = () => {
  const [modifyProviderConfiguration, setModifyProviderConfiguration] = useState<
    TemplateContract<ProviderConfiguration> | undefined
  >();

  const configurations = useRegistryProviderConfigurations();
  const providerService = useProviderService();
  const archiveProviderConfiguration = useArchiveProviderConfiguration();
  const { party } = useParties();
  const { showBoundary } = useErrorBoundary<Error>();

  const archive = useCallback(
    (row: Row) => {
      if (!providerService.data) {
        showBoundary(
          new Error(ERR_ARC_PROVIDER_CONF, { cause: 'Provider service is not available.' }),
        );
        return Promise.resolve();
      }
      return archiveProviderConfiguration({
        cid: providerService.data.contractId,
        argument: { cid: row.contractId },
      });
    },
    [providerService, archiveProviderConfiguration, showBoundary],
  );

  const createActions = (r: Row) => {
    const actions: Action[] = [];
    if (r.payload.provider === party)
      actions.push(
        { name: 'Modify', action: () => Promise.resolve(setModifyProviderConfiguration(r)) },
        { name: 'Archive', action: archive },
      );
    return actions;
  };

  const isLoading = configurations.isLoading || providerService.isLoading;
  if (isLoading) return <Loading />;

  return (
    <PageWrapper pageName="">
      {!!modifyProviderConfiguration && (
        <ModifyProviderConfigurationDialog
          open={!!modifyProviderConfiguration}
          onClose={() => setModifyProviderConfiguration(undefined)}
          providerConfiguration={modifyProviderConfiguration}
        />
      )}
      <DataTable
        columns={createHeaders(createActions)}
        rows={configurations.data || []}
        rowKey={(a) => a.contractId}
        toolbar={Toolbar}
        getRowHeight={() => 'auto'}
      />
    </PageWrapper>
  );
};
